<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useBlockSafe } from '@/composables/useBlockSafe';
import Buttons from '@/components/ui/Buttons.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  template,
  title,
  description,
  image,
  buttons,
  hasTitle,
  hasDescription,
  hasImage,
  hasButtons,
  hasAnyContent,
} = useBlockSafe(props.data);
// refs
const mediaRef = ref(null);
const parallaxRef = ref(null);

// mobile check
const isMobile = ref(false);
const checkMobile = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches;
};

// easing (Apple-like плавність)
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// parallax engine
let rafId = null;
let isActive = false;

const updateParallax = () => {
  if (!mediaRef.value || !parallaxRef.value) return;

  const rect = mediaRef.value.getBoundingClientRect();
  const windowHeight = window.innerHeight;

  let progress = (windowHeight - rect.top) / (windowHeight + rect.height);
  progress = Math.max(0, Math.min(1, progress));

  const eased = easeOutCubic(progress);

  const maxOffsetPercent = 25;
  const translateY = (eased - 0.5) * maxOffsetPercent;

  parallaxRef.value.style.transform = `
  translateY(calc(-50% + ${translateY}%))
`;
};

const loop = () => {
  if (!isActive) return;

  rafId = requestAnimationFrame(() => {
    updateParallax();
    loop();
  });
};

// intersection observer
let observer = null;

const initObserver = () => {
  if (!mediaRef.value) return;

  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        isActive = true;
        loop();
      } else {
        isActive = false;
        if (rafId) cancelAnimationFrame(rafId);
      }
    },
    { threshold: 0 },
  );

  observer.observe(mediaRef.value);
};

// lifecycle
onMounted(() => {
  if (template.value !== 'default') return;

  checkMobile();
  if (isMobile.value) return;

  initObserver();

  window.addEventListener('resize', checkMobile, { passive: true });
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
  if (rafId) cancelAnimationFrame(rafId);

  window.removeEventListener('resize', checkMobile);
});
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        :class="[
          'page-hero',
          !hasButtons ? 'page-hero--without-buttons ' : '',
          !hasDescription ? 'page-hero--without-description ' : '',
          !hasButtons && !hasDescription ? 'page-hero--only-title' : '',
          `page-hero--${template}`,
        ]"
      >
        <div class="page-hero__top">
          <h1
            v-if="hasTitle"
            class="page-hero__title h1"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="title"
          ></h1>
          <div
            v-if="hasDescription"
            class="page-hero__description body-3-regular"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="description"
          ></div>
          <Buttons
            v-if="hasButtons"
            :background-color="backgroundColor"
            :buttons="buttons"
            class="page-hero__buttons"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :buttons-prefix-class="'js-button-page-hero'"
          />
        </div>
        <div
          v-if="hasImage"
          ref="mediaRef"
          class="page-hero__media"
          data-aos="fade-left"
        >
          <div class="page-hero__parallax-outer">
            <div ref="parallaxRef" class="page-hero__parallax">
              <NuxtImg
                :src="image.src"
                :alt="image.alt"
                :width="image.width"
                :height="image.height"
                class="page-hero__image"
                preload
                fetchpriority="high"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.page-hero {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(sm) {
    gap: 64px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 64);
  }
  &__top {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas: 'title' 'description' 'buttons';
    gap: 32px;
    @include media-breakpoint-up(sm) {
      align-items: start;
      column-gap: 24px;
      grid-template-columns: 4.87fr 7.13fr;
      grid-template-areas: 'title title' 'buttons description';
      row-gap: 48px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 8fr 4fr;
      grid-template-areas: 'title description' 'buttons description';
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 24);
      @include responsive-spacing(row-gap, 48);
    }
  }
  &--without-buttons {
    #{$parent} {
      &__top {
        grid-template-areas: 'title' 'description';
        @include media-breakpoint-up(sm) {
          grid-template-columns: 7.13fr 4.87fr;
          grid-template-areas: 'title title' 'description .';
        }
        @include media-breakpoint-up(md) {
          grid-template-columns: 8fr 4fr;
          grid-template-areas: 'title description';
        }
      }
    }
  }
  &--without-description {
    #{$parent} {
      &__top {
        grid-template-areas: 'title' 'buttons';
        @include media-breakpoint-up(sm) {
          grid-template-areas: 'title title' 'buttons .';
        }
        @include media-breakpoint-up(md) {
          grid-template-areas: 'title .' 'buttons .';
        }
      }
    }
  }
  &--only-title {
    #{$parent} {
      &__top {
        grid-template-areas: 'title';
        @include media-breakpoint-up(sm) {
          grid-template-areas: 'title';
          grid-template-columns: 1fr;
        }
        @include media-breakpoint-up(md) {
          grid-template-areas: 'title .';
        }
      }
    }
  }
  &__title {
    margin: 0;
    grid-area: title;
  }
  &__description {
    grid-area: description;
    &:deep(p:last-child) {
      margin: 0;
    }
    @include media-breakpoint-up(md) {
      align-self: self-end;
    }
  }
  &__buttons {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    justify-content: flex-center;
    gap: 8px;
    grid-area: buttons;
    @include media-breakpoint-up(md) {
      gap: 16px;
      flex-direction: row;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &__button {
    width: fit-content;
  }
  &__media {
    aspect-ratio: 360/288;
    margin: 0 -16px;
    width: calc(100% + 32px);
    overflow: hidden;
    position: relative;
    @include media-breakpoint-up(sm) {
      aspect-ratio: 768/480;
      margin: 0 -24px;
      width: calc(100% + 48px);
    }
    @include media-breakpoint-up(md) {
      aspect-ratio: unset;
      margin: 0 -48px;
      height: 480px;
      width: calc(100% + 96px);
    }
    @include media-breakpoint-up(xl) {
      margin: 0 px-to-vw(-48);
      @include responsive-spacing(height, 480);
      width: calc(100% + px-to-vw(96));
    }
  }
  &__parallax {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 125%;

    transform: translateY(-50%);
    &-outer {
      position: absolute;
      inset: 0;
      overflow: hidden;
    }
  }

  &__image {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
  &--two-columns {
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(md) {
      display: grid;
      grid-template-columns: 6fr 6fr;
      gap: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
    #{$parent} {
      &__media {
        aspect-ratio: 328/245;
        order: 1;
        margin: 0;
        width: 100%;
        @include media-breakpoint-up(md) {
          aspect-ratio: unset;
          height: 100%;
          order: 2;
        }
      }
      &__image {
        border-radius: 8px;
        @include media-breakpoint-up(sm) {
          margin-left: auto;
          width: 100%;
          height: auto;
        }
        @include media-breakpoint-up(md) {
          width: 81%;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 8);
        }
      }
      &__top {
        order: 2;
        @include media-breakpoint-up(sm) {
          align-items: start;
          column-gap: 16px;
          grid-template-columns: 7.13fr 4.87fr;
          grid-template-areas: 'title title' 'description buttons';
          row-gap: 48px;
        }
        @include media-breakpoint-up(md) {
          order: 1;
          grid-template-columns: 1fr;
          grid-template-areas: 'title' 'description' 'buttons';
          row-gap: 62px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(column-gap, 16);
          @include responsive-spacing(row-gap, 62);
        }
      }
      &__title {
        text-align: center;
        @include media-breakpoint-up(sm) {
          text-align: left;
        }
      }
      &__description {
        text-align: center;
        @include media-breakpoint-up(sm) {
          text-align: left;
        }
        @include media-breakpoint-up(md) {
          align-self: auto;
          max-width: 67%;
        }
      }
      &__buttons {
        @include media-breakpoint-up(sm) {
          align-items: end;
        }
      }
    }
  }
}
</style>
